import type { ConstValues } from '@openx/types/helpers';
import type { Reducer } from 'react';

export type PaginationState<T> = {
  offset: number;
  items: T[];
  totalCount?: number;
  isManualLoading: boolean;
};

export const ActionType = {
  Reset: 'reset',
  SetOffset: 'set-offset',
  StartLoading: 'start-loading',
  FinishLoading: 'finish-loading',
  AppendItems: 'append-items',
  SetTotalCount: 'set-total-count',
} as const;
export type ActionType = ConstValues<typeof ActionType>;

type ResetAction<T> = { type: typeof ActionType.Reset; items: T[]; totalCount?: number };
type SetOffsetAction = { type: typeof ActionType.SetOffset; offset: number };
type StartLoadingAction = { type: typeof ActionType.StartLoading };
type FinishLoadingAction = { type: typeof ActionType.FinishLoading };
type AppendItemsAction<T> = { type: typeof ActionType.AppendItems; items: T[]; totalCount?: number };
type SetTotalCountAction = { type: typeof ActionType.SetTotalCount; totalCount: number };

export type PaginationAction<T> =
  | ResetAction<T>
  | SetOffsetAction
  | StartLoadingAction
  | FinishLoadingAction
  | AppendItemsAction<T>
  | SetTotalCountAction;

export const createInitialPaginationState = <T>(initialItems?: T[], initialTotal?: number): PaginationState<T> => ({
  offset: 0,
  items: initialItems || [],
  totalCount: initialTotal,
  isManualLoading: false,
});

export const paginationReducer = <T>(): Reducer<PaginationState<T>, PaginationAction<T>> => {
  return (state: PaginationState<T>, action: PaginationAction<T>): PaginationState<T> => {
    switch (action.type) {
      case ActionType.Reset:
        return {
          ...state,
          offset: 0,
          items: action.items,
          totalCount: action.totalCount ?? state.totalCount,
          isManualLoading: false,
        };

      case ActionType.SetOffset:
        return {
          ...state,
          offset: action.offset,
        };

      case ActionType.StartLoading:
        return {
          ...state,
          isManualLoading: true,
        };

      case ActionType.FinishLoading:
        return {
          ...state,
          isManualLoading: false,
        };

      case ActionType.AppendItems:
        return {
          ...state,
          items: [...state.items, ...action.items],
          totalCount: action.totalCount ?? state.totalCount,
        };

      case ActionType.SetTotalCount:
        return {
          ...state,
          totalCount: action.totalCount,
        };

      default:
        return state;
    }
  };
};
